import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@shared/design-system/ui'
import { HlsVideoPlayer } from '@shared/ui/HlsVideoPlayer'
import { useCompleteMemberLesson, useMemberLessonQuery, useRecordMemberProgress } from '../api/member-access.queries'
import { memberApiAuthHeaders } from '../api/member-access.api'
import { useMemberSession } from '../model/member-session-store'
import { LessonPlayerLayout } from './LessonPlayerLayout'
import { MemberAccessLayout } from './MemberAccessLayout'
import { MemberCourseTree } from './MemberCourseTree'
import { MemberFileCard } from './MemberFileCard'
import { crumb, gate, lessonBack, lessonBody, muted, subtitle, title } from './member-access.css'

export function MemberLessonPage({
  producerId,
  productId,
  lessonId,
}: {
  producerId: string
  productId: string
  lessonId: string
}) {
  const navigate = useNavigate()
  const token = useMemberSession((state) => state.token)
  const lesson = useMemberLessonQuery(productId, lessonId, Boolean(token))
  const progress = useRecordMemberProgress(productId, lessonId)
  const complete = useCompleteMemberLesson(productId, lessonId)

  if (!token) {
    void navigate({ to: '/acesso/$producerId', params: { producerId } })
    return null
  }
  if (lesson.isError) {
    void navigate({ to: '/acesso/$producerId/cursos/$productId', params: { producerId, productId } })
    return null
  }
  const view = lesson.data
  const nextLessonId = view?.nextLessonId
  const playbackUrl = view?.lesson.kind === 'video' ? view.lesson.playbackUrl : null
  if (view?.completedCourse) {
    return (
      <MemberAccessLayout producerName={view.course.producerName} logoUrl={view.course.logoUrl}>
        <section className={gate} data-testid="member-congrats">
          <h1 className={title}>Parabéns</h1>
          <p className={subtitle}>Você concluiu {view.course.product.name}.</p>
          <Link to="/acesso/$producerId/biblioteca" params={{ producerId }}>
            <Button>Voltar aos conteúdos</Button>
          </Link>
        </section>
      </MemberAccessLayout>
    )
  }

  return (
    <MemberAccessLayout
      variant="player"
      producerName={view?.course.producerName ?? 'Area de membros'}
      logoUrl={view?.course.logoUrl}
    >
      <LessonPlayerLayout
        advanceKey={lessonId}
        title={view?.lesson.title ?? ''}
        back={
          <Link
            className={lessonBack}
            to="/acesso/$producerId/biblioteca"
            params={{ producerId }}
            aria-label="Voltar ao catálogo"
            data-testid="lesson-back"
          >
            <ArrowLeft size={18} />
          </Link>
        }
        course={<span className={crumb}>{view?.course.product.name ?? 'Curso'}</span>}
        onAdvance={
          nextLessonId
            ? () =>
                void navigate({
                  to: '/acesso/$producerId/cursos/$productId/aulas/$lessonId',
                  params: { producerId, productId, lessonId: nextLessonId },
                })
            : undefined
        }
        video={
          playbackUrl
            ? ({ onEnded }) => (
                <HlsVideoPlayer
                  flush
                  transport
                  src={`/api${playbackUrl}`}
                  title={view?.lesson.title ?? ''}
                  startAt={view?.lesson.positionSeconds}
                  requestHeaders={(url) => memberApiAuthHeaders(url)}
                  onEnded={onEnded}
                  onTime={(seconds, duration) => {
                    progress.mutate({ positionSeconds: seconds, durationSeconds: duration })
                  }}
                />
              )
            : undefined
        }
        previous={
          view?.previousLessonId ? (
            <Link
              to="/acesso/$producerId/cursos/$productId/aulas/$lessonId"
              params={{ producerId, productId, lessonId: view.previousLessonId }}
            >
              <Button variant="secondary">Aula anterior</Button>
            </Link>
          ) : null
        }
        next={
          view?.nextLessonId ? (
            <Link
              to="/acesso/$producerId/cursos/$productId/aulas/$lessonId"
              params={{ producerId, productId, lessonId: view.nextLessonId }}
            >
              <Button>Próxima aula</Button>
            </Link>
          ) : null
        }
        tree={
          <MemberCourseTree
            placement="rail"
            currentLessonId={lessonId}
            producerId={producerId}
            productId={productId}
            modules={view?.course.modules ?? []}
          />
        }
      >
        {view?.lesson.body ? (
          <p className={lessonBody}>{view.lesson.body}</p>
        ) : view?.lesson.kind !== 'video' ? (
          <p className={muted}>Esta aula ainda não tem conteúdo.</p>
        ) : null}
        {view?.lesson.assets.map((asset) => (
          <MemberFileCard
            key={asset.id}
            name={asset.name}
            sizeBytes={asset.sizeBytes}
            onOpen={() => {
              window.open(asset.downloadUrl, '_blank', 'noopener,noreferrer')
            }}
          />
        ))}
        {view && view.lesson.kind !== 'video' ? (
          <Button onClick={() => complete.mutate()} disabled={view.lesson.completed}>
            {view.lesson.completed ? 'Aula concluída' : 'Marcar como concluída'}
          </Button>
        ) : null}
      </LessonPlayerLayout>
    </MemberAccessLayout>
  )
}
